import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const features = [
  { icon: 'ri-scissors-line', title: 'Bespoke Tailors (100% Custom Made)', desc: 'Every suit is cut and sewn specifically for your body measurements.' },
  { icon: 'ri-palette-line', title: '5000+ Fabrics', desc: 'Choose from an extensive library of premium Italian and British wools.' },
  { icon: 'ri-user-line', title: 'Personal Stylist', desc: 'One-on-one consultation with an expert clothier from start to finish.' },
  { icon: 'ri-time-line', title: '6–8 Week Turnaround', desc: 'Crafted with care and delivered on time for your most important moments.' },
  { icon: 'ri-home-line', title: 'House Calls Available', desc: 'We make house and office calls for a truly convenient experience.' },
];

const occasions = [
  { label: 'Weddings', img: 'https://public.readdy.ai/ai/img_res/8271f4930596f474e2b44223976d6da9.jpg' },
  { label: 'Business', img: 'https://readdy.ai/api/search-image?query=professional%20custom%20business%20suit%20charcoal%20grey%20tailored%20fit%20white%20shirt%20tie%20office%20setting%20premium%20menswear%20photography&width=400&height=500&seq=cs2&orientation=portrait' },
  { label: 'Black Tie', img: 'https://readdy.ai/api/search-image?query=luxury%20black%20tie%20tuxedo%20custom%20tailored%20formal%20evening%20suit%20white%20pocket%20square%20elegant%20menswear%20studio%20photography&width=400&height=500&seq=cs3&orientation=portrait' },
];

const faqs = [
  { q: 'How long does a custom suit take?', a: 'Typically 6–8 week turnaround from your first fitting to final delivery, depending on fabric availability and complexity.' },
  { q: 'Can you come to my home or office?', a: 'Absolutely. We make house and office calls throughout Massachusetts and New England.' },
];

const CustomSuitsPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero */}
      <section className="relative h-[70vh] min-h-[500px] flex items-center justify-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=luxury%20custom%20suit%20tailor%20shop%20bespoke%20menswear%20wellesley%20massachusetts%20premium%20fabric%20rolls%20elegant%20interior%20warm%20lighting%20professional%20clothier&width=1400&height=700&seq=csh1&orientation=landscape"
          alt="Custom Suits"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900/85 to-gray-900/40"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-[#C4A962] font-semibold tracking-widest uppercase text-sm mb-4">Custom Clothiers — Wellesley, MA</p>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
            Custom Suits<br />Built for You
          </h1>
          <p className="text-xl text-gray-200 max-w-xl mb-8">
            Bespoke suits crafted to your exact measurements, style, and personality. Experience the difference of true Bespoke Tailors (100% Custom Made) tailoring.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to="/contact" className="bg-[#C4A962] text-white px-8 py-4 rounded-lg font-semibold hover:bg-[#B39952] transition-colors whitespace-nowrap">
              Call for Appointment
            </Link>
            <a href="tel:781-237-3166" className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-gray-900 transition-colors whitespace-nowrap">
              <i className="ri-phone-line mr-2"></i>781-237-3166
            </a>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose a Custom Suit?</h2>
            <p className="text-lg text-gray-500 max-w-2xl mx-auto">Every detail is tailored to you — from the lapel width to the lining color.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((f) => (
              <div key={f.title} className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <div className="w-12 h-12 flex items-center justify-center bg-[#C4A962]/10 rounded-xl mb-5">
                  <i className={`${f.icon} text-[#C4A962] text-2xl`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{f.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Occasions */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Suits for Every Occasion</h2>
            <p className="text-lg text-gray-500">From boardroom to black tie — we have you covered.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {occasions.map((o) => (
              <div key={o.label} className="relative rounded-2xl overflow-hidden group cursor-pointer">
                <img src={o.img} alt={o.label} className="w-full h-80 object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/70 to-transparent"></div>
                <div className="absolute bottom-6 left-6">
                  <span className="text-white text-2xl font-bold">{o.label}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-4xl font-bold mb-4">The Custom Suit Process</h2>
            <p className="text-gray-400 text-lg">Simple, personal, and enjoyable from start to finish.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Consultation', desc: 'Discuss your style, occasion, and preferences with your personal clothier.' },
              { step: '02', title: 'Fabric Selection', desc: 'Browse 5000+ fabrics available for suits and choose your lining, buttons, and details.' },
              { step: '03', title: 'Measurements', desc: 'We take 30+ precise measurements to ensure a perfect fit.' },
              { step: '04', title: 'Final Fitting', desc: 'Try on your finished suit and receive any final adjustments.' },
            ].map((s) => (
              <div key={s.step} className="text-center">
                <div className="text-5xl font-bold text-[#C4A962]/30 mb-3">{s.step}</div>
                <h3 className="text-xl font-bold mb-2">{s.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-6">
            {faqs.map((faq) => (
              <div key={faq.q} className="border border-gray-200 rounded-xl p-6">
                <h3 className="font-bold text-gray-900 mb-2">{faq.q}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#C4A962]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-white mb-4">Ready for Your Perfect Suit?</h2>
          <p className="text-white/80 text-lg mb-8">Call for appointment at our Wellesley showroom or request a house call.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="bg-white text-[#C4A962] px-8 py-4 rounded-lg font-bold hover:bg-gray-100 transition-colors whitespace-nowrap">
              Call for Appointment
            </Link>
            <a href="tel:781-237-3166" className="border-2 border-white text-white px-8 py-4 rounded-lg font-bold hover:bg-white/10 transition-colors whitespace-nowrap">
              Call 781-237-3166
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CustomSuitsPage;